// Brief draft revisions — Wave B.
//
// AI-generated drafts and user-edited revisions live in the same
// `content_piece_drafts` table, distinguished by `revision_kind`.
// AI rows: revision_kind = 'ai', body in `draft_body_encrypted`.
// User edits: revision_kind = 'user_edit', body in `draft_doc` (JSONB BriefDocument).

import { supabase } from "@/integrations/supabase/client";
import type { BriefDocument } from "@/lib/briefDocument";
import { plainTextToBriefDocument } from "@/lib/briefDocument";
import type { ContentBriefDraft } from "@/lib/composerCore";

export type DraftRevisionKind = "ai" | "user_edit";

/** Extra columns added in Wave B on top of the legacy ContentBriefDraft. */
export type DraftRevisionExtras = {
  revision_kind: DraftRevisionKind;
  draft_doc: unknown | null;
  parent_draft_id: string | null;
  last_edited_by_user_id: string | null;
};

export type ContentBriefDraftRevision = ContentBriefDraft & DraftRevisionExtras;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const briefDraftsTable = () => (supabase as any).from("content_piece_drafts");

const emptyDoc: BriefDocument = { type: "doc", content: [{ type: "paragraph" }] };

/** Create a fresh user-edit revision seeded from an AI draft. */
export async function createUserEditRevision(args: {
  workspaceId: string;
  briefId: string;
  source: ContentBriefDraft | ContentBriefDraftRevision | null;
}): Promise<ContentBriefDraftRevision> {
  const { workspaceId, briefId, source } = args;
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData?.user) throw new Error("You must be signed in to edit drafts.");
  const userId = userData.user.id;

  // Seed from AI text (or start blank).
  const sourceText = source?.draft_body ?? null;
  const seedDoc: BriefDocument = sourceText && sourceText.trim().length > 0
    ? plainTextToBriefDocument(sourceText)
    : emptyDoc;

  // Seed the title: prefer the source's existing title, otherwise derive
  // from the first non-empty line of the seeded doc (mirrors docToTitle in
  // components/composer/draft/draftCanvasUtils.ts; inlined to avoid a
  // lib → components import).
  const deriveTitleFromDoc = (doc: unknown): string => {
    try {
      const root = doc as { content?: Array<{ content?: Array<{ text?: string }> }> };
      for (const block of root?.content ?? []) {
        const text = (block.content ?? []).map((c) => c.text ?? "").join("").trim();
        if (text) return text.slice(0, 80);
      }
    } catch {
      /* noop */
    }
    return "";
  };
  const existingTitle = source?.draft_title?.trim() ?? "";
  const derivedTitle = existingTitle || deriveTitleFromDoc(seedDoc);
  const seededTitle = derivedTitle.length > 0 ? derivedTitle : null;

  const insertRow = {
    workspace_id: workspaceId,
    piece_id: briefId,
    plan_id: source?.plan_id ?? null,
    requested_by_user_id: userId,
    status: "succeeded" as const,
    revision_kind: "user_edit" as const,
    draft_doc: seedDoc as unknown,
    parent_draft_id: source?.id ?? null,
    last_edited_by_user_id: userId,
    draft_title: seededTitle,
    draft_summary: source?.draft_summary ?? null,
    revision_notes: source ? `Edited from AI draft ${source.id.slice(0, 8)}` : "Manual draft",
    started_at: new Date().toISOString(),
    completed_at: new Date().toISOString(),
    context_snapshot: source?.context_snapshot ?? {},
  };


  const { data, error } = await briefDraftsTable().insert(insertRow).select("*").single();
  if (error) throw new Error(error.message || "Failed to create edit revision");
  return data as ContentBriefDraftRevision;
}

/** Update the JSON body and/or title of a user-edit revision. */
export async function saveUserEditRevision(args: {
  draftId: string;
  doc?: BriefDocument | unknown;
  title?: string | null;
}): Promise<ContentBriefDraftRevision> {
  const { draftId, doc, title } = args;
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id ?? null;

  const patch: Record<string, unknown> = {
    last_edited_by_user_id: userId,
    updated_at: new Date().toISOString(),
  };
  if (doc !== undefined) patch.draft_doc = doc as unknown;
  if (title !== undefined) patch.draft_title = title;

  const { data, error } = await briefDraftsTable()
    .update(patch)
    .eq("id", draftId)
    .eq("revision_kind", "user_edit")
    .select("*")
    .maybeSingle();

  if (error) throw new Error(error.message || "Failed to save edits");
  if (!data) throw new Error("Couldn't save edits because the target draft is not editable.");
  return data as ContentBriefDraftRevision;
}

/** Hard-delete a draft (AI or user_edit). Scoped by RLS to the caller's workspace. */
export async function deleteDraft(args: { draftId: string }): Promise<void> {
  const { draftId } = args;
  const { error } = await briefDraftsTable().delete().eq("id", draftId);
  if (error) throw new Error(error.message || "Failed to delete draft");
}

/**
 * Persist a new ordering of drafts for the right-rail Drafts queue.
 * Writes `sort_order = index + 1` for each id in the array.
 * Rows not listed keep their existing `sort_order`.
 */
export async function reorderDrafts(orderedDraftIds: string[]): Promise<void> {
  await Promise.all(
    orderedDraftIds.map((id, index) =>
      briefDraftsTable().update({ sort_order: index + 1 }).eq("id", id),
    ),
  );
}


